import React, { useEffect, useRef, useState } from 'react';
import { generateDeviceId, isMobile, isKakaoInAppBrowser } from '../utils/deviceUtils';
import { submitArcadeScore, loadTopArcadeScores, getDeviceLinkInfo, registerShareParticipant } from '../firebase';
import ShareBrowserModal from './ShareBrowserModal';
// MessageCircle, Type, Image removed

const ArcadeModal = ({ isOpen, onClose }) => {
    const canvasRef = useRef(null);
    const [selectedGame, setSelectedGame] = useState(null); // null: 선택 화면, 'falling-dots': Falling Dots
    const [score, setScore] = useState(0);
    const [, setRunning] = useState(false); // running removed (unused value)
    const touchState = useRef({ left: false, right: false });
    const [restartSeq, setRestartSeq] = useState(0);
    const [, setGameOver] = useState(false); // gameOver removed (unused value)
    const [rankings, setRankings] = useState([]); // {name, score, ts}[]
    const [playerName, setPlayerName] = useState('');
    const [showResults, setShowResults] = useState(false);
    const [showShareBrowserModal, setShowShareBrowserModal] = useState(false);
    const [finalScore, setFinalScore] = useState(0); // 게임 종료 시점의 최종 점수 저장
    const [currentGameName, setCurrentGameName] = useState(''); // 현재 게임 이름 (공유용)
    // bulkKakao vars removed
    const [isSavingRanking, setIsSavingRanking] = useState(false); // 랭킹 저장 중복 방지 플래그
    const [savedScore, setSavedScore] = useState(null); // 이미 저장된 점수 추적

    // 플레이어 이름만 초기화
    useEffect(() => {
        const id = generateDeviceId().slice(0, 6);
        const savedName = localStorage.getItem('arcadeName') || id;
        setPlayerName(savedName);
    }, [isOpen]);

    // 모달이 열릴 때 게임 선택 화면으로 리셋 및 배경 스크롤 방지
    useEffect(() => {
        if (isOpen) {
            setSelectedGame(null);
            setScore(0);
            setGameOver(false);
            setShowResults(false);
            setIsSavingRanking(false);
            setSavedScore(null);

            // 배경 스크롤 방지
            const originalOverflow = document.body.style.overflow;
            document.body.style.overflow = 'hidden';

            return () => {
                // 모달이 닫힐 때 원래 스크롤 상태로 복원
                document.body.style.overflow = originalOverflow;
            };
        }
    }, [isOpen]);

    const saveRanking = React.useCallback(async (sc) => {
        // 중복 저장 방지: 이미 저장 중이거나 동일한 점수로 이미 저장된 경우 중단
        if (isSavingRanking) {
            console.log('랭킹 저장 중입니다. 중복 저장을 방지합니다.');
            return;
        }
        
        // 이미 저장된 경우 중단
        if (savedScore === sc) {
            console.log('이미 저장된 점수입니다. 중복 저장을 방지합니다.');
            return;
        }

        setIsSavingRanking(true);
        
        // 사용자 이름 가져오기
        let nameToUse = '';
        const deviceId = localStorage.getItem('deviceId') || generateDeviceId();

        // 1순위: 랭킹 모달에서 입력한 닉네임 (playerName) 우선 사용
        if (playerName && playerName.trim()) {
            nameToUse = playerName.trim();
            // 체험 사용자인 경우 DeviceID 사용
            if (nameToUse === '체험 사용자') {
                nameToUse = deviceId;
            }
        }

        // 2순위: localStorage에서 userInfo 확인
        if (!nameToUse) {
            try {
                const savedUserInfo = localStorage.getItem('userInfo');
                if (savedUserInfo) {
                    const userInfo = JSON.parse(savedUserInfo);
                    if (userInfo && userInfo.name && userInfo.name.trim()) {
                        const nameFromLocal = userInfo.name.trim();
                        // 체험 사용자인 경우 DeviceID 사용
                        if (nameFromLocal === '체험 사용자') {
                            nameToUse = deviceId;
                        } else {
                            nameToUse = nameFromLocal;
                        }
                    }
                }
            } catch (error) {
                console.error('사용자 정보 로드 실패:', error);
            }
        }

        // 3순위: 기기 링크 정보에서 확인
        if (!nameToUse) {
            try {
                const deviceLinkInfo = await getDeviceLinkInfo(deviceId);
                if (deviceLinkInfo && deviceLinkInfo.userInfo && deviceLinkInfo.userInfo.name && deviceLinkInfo.userInfo.name.trim()) {
                    const nameFromDevice = deviceLinkInfo.userInfo.name.trim();
                    // 체험 사용자인 경우 DeviceID 사용
                    if (nameFromDevice === '체험 사용자') {
                        nameToUse = deviceId;
                    } else {
                        nameToUse = nameFromDevice;
                    }
                }
            } catch (error) {
                console.error('기기 링크 정보 로드 실패:', error);
            }
        }

        // 사용자 이름이 없으면 DeviceID 사용
        if (!nameToUse) {
            nameToUse = deviceId;
        }

        const gameType = selectedGame || 'falling-dots';
        const ok = await submitArcadeScore(nameToUse, sc, gameType);
        if (ok) {
            setFinalScore(sc); // 최종 점수 저장
            setSavedScore(sc); // 저장된 점수 기록
            const top = await loadTopArcadeScores(10, gameType);
            if (Array.isArray(top)) {
                setRankings(top.map(r => ({ name: r.name, score: r.score, ts: r.createdAt?.toMillis?.() || r.createdAt || Date.now() })));
                setShowResults(true);
            }
        }
        
        setIsSavingRanking(false);
    }, [isSavingRanking, savedScore, playerName, selectedGame]);

    // 게임 결과 공유하기 함수
    const handleShareGameResult = async () => {
        // 모바일이 아니면 공유하지 않음
        if (!isMobile()) {
            alert('모바일 기기에서만 공유할 수 있습니다.');
            return;
        }

        // 카카오톡 인앱 브라우저인 경우 브라우저 안내 모달 표시
        if (isKakaoInAppBrowser()) {
            setShowShareBrowserModal(true);
            return;
        }

        // 앱 공유 프로모션에 등록 (이름과 전화번호가 있는 경우)
        let shareDeviceId = null;
        try {
            const deviceId = localStorage.getItem('deviceId') || generateDeviceId();
            let userName = '';
            let userPhone = '';

            // 사용자 정보 가져오기
            try {
                const savedUserInfo = localStorage.getItem('userInfo');
                if (savedUserInfo) {
                    const userInfo = JSON.parse(savedUserInfo);
                    if (userInfo && userInfo.name && userInfo.name.trim() && userInfo.name.trim() !== '체험 사용자') {
                        userName = userInfo.name.trim();
                    }
                    if (userInfo && userInfo.phone && userInfo.phone.trim()) {
                        userPhone = userInfo.phone.trim().replace(/\s/g, '');
                    }
                }
            } catch (error) {
                console.error('사용자 정보 로드 실패:', error);
            }

            // 기기 링크 정보에서 확인
            if (!userName || !userPhone) {
                try {
                    const deviceLinkInfo = await getDeviceLinkInfo(deviceId);
                    if (deviceLinkInfo && deviceLinkInfo.userInfo) {
                        if (!userName && deviceLinkInfo.userInfo.name && deviceLinkInfo.userInfo.name.trim() && deviceLinkInfo.userInfo.name.trim() !== '체험 사용자') {
                            userName = deviceLinkInfo.userInfo.name.trim();
                        }
                        if (!userPhone && deviceLinkInfo.userInfo.phone && deviceLinkInfo.userInfo.phone.trim()) {
                            userPhone = deviceLinkInfo.userInfo.phone.trim().replace(/\s/g, '');
                        }
                    }
                } catch (error) {
                    console.error('기기 링크 정보 로드 실패:', error);
                }
            }

            // 이름과 전화번호가 모두 있으면 공유 프로모션에 등록
            if (userName && userPhone && userPhone.length >= 10) {
                try {
                    const parentDeviceId = localStorage.getItem('shareReferrer') || null;
                    const registerResult = await registerShareParticipant({
                        deviceId,
                        name: userName,
                        phone: userPhone,
                        parentDeviceId
                    });

                    if (registerResult && registerResult.data && registerResult.data.success) {
                        shareDeviceId = deviceId;
                        console.log('앱 공유 프로모션에 등록되었습니다.');
                    }
                } catch (error) {
                    console.error('공유 프로모션 등록 실패:', error);
                    // 실패해도 공유는 계속 진행
                }
            }
        } catch (error) {
            console.error('공유 프로모션 등록 중 오류:', error);
        }

        // 공유 메시지 생성
        const displayName = playerName.trim() || '익명';
        const shareUrl = shareDeviceId ? `https://fcfunnel.com/intro?ref=${shareDeviceId}` : 'https://fcfunnel.com/intro';
        const gameTitle = currentGameName || '게임';
        const shareText = `${displayName}님이 ${finalScore}점을 달성했습니다! 🎯

FC퍼널 앱의 오락실에서 플레이할 수 있는 재미있는 게임이에요!

FC퍼널은 영업이 쉬워지는 순간을 만들어주는 스마트 고객관리 앱입니다. 체계적인 단계별 고객관리부터 생일 알림, 할일 자동 알림, 상세 메모, 지도 기능까지 모든 영업 활동을 한 곳에서 관리할 수 있어요. 7일 무료체험으로 지금 바로 시작해보세요!

나도 도전해볼까? 👇`;
        const shareMessageForClipboard = `${shareText}\n${shareUrl}`; // 폴백용 (링크 포함)

        // Web Share API 사용 시도
        if (navigator.share) {
            try {
                await navigator.share({
                    title: `🎮 ${gameTitle} 게임 결과 공유! 🎮`,
                    text: shareText,
                    url: shareUrl
                });
                return;
            } catch (shareError) {
                // 사용자가 취소하거나 오류 발생 시 폴백으로 진행
                if (shareError.name !== 'AbortError') {
                    console.error('Web Share API 오류:', shareError);
                }
            }
        }

        // 폴백: 클립보드 복사 후 카카오톡 열기
        try {
            await navigator.clipboard.writeText(shareMessageForClipboard);
        } catch (clipError) {
            // 클립보드 API 실패 시 폴백
            const textArea = document.createElement('textarea');
            textArea.value = shareMessageForClipboard;
            textArea.style.position = 'fixed';
            textArea.style.opacity = '0';
            document.body.appendChild(textArea);
            textArea.select();
            document.execCommand('copy');
            document.body.removeChild(textArea);
        }

        // 카카오톡 앱 열기 (비동기로 처리하여 모달이 먼저 닫히도록)
        setTimeout(() => {
            try {
                window.location.href = 'kakaotalk://';
            } catch (e) {
                // 카카오톡 앱이 없는 경우 무시
            }
        }, 100);

        // 사용자에게 알림
        alert('메시지를 복사했습니다. 카카오톡이 열리면 붙여넣기 하세요.');
    };

    // Falling Dots 게임
    useEffect(() => {
        if (!isOpen || selectedGame !== 'falling-dots') return;
        setCurrentGameName('Falling Dots');
        const canvas = canvasRef.current;
        const ctx = canvas.getContext('2d');
        let rafId;
        const startedAt = performance.now();

        // Game state: simple dodge game (responsive)
        const getViewportHeight = () => (window.visualViewport && window.visualViewport.height) ? window.visualViewport.height : window.innerHeight;
        const getSize = () => {
            const vw = Math.min(window.innerWidth, 460);
            const vh = getViewportHeight();
            // header(40) + caption(24) + buttons(64) + gaps(48) + safe-area(24) ≈ 200
            const reserved = 200;
            const h = Math.max(280, Math.min(vh - reserved, 760));
            return { W: vw, H: h };
        };
        let { W, H } = getSize();
        canvas.width = W; canvas.height = H;
        let player = { x: W / 2, y: H - 40, r: 10, vx: 0 };
        let obstacles = [];
        let lastSpawn = 0;
        let localScore = 0;
        let alive = true;
        let finalized = false;

        const spawn = (difficulty = 1) => {
            const x = Math.random() * (W - 20) + 10;
            const r = 6 + Math.random() * 10;
            // 속도는 난이도에 따라 가중
            const vy = (2 + Math.random() * 3) * difficulty;
            obstacles.push({ x, y: -r, r, vy });
        };

        const loop = (t) => {
            if (!alive) return;
            ctx.clearRect(0, 0, W, H);
            // background
            ctx.fillStyle = '#0f172a';
            ctx.fillRect(0, 0, W, H);

            // 난이도: 시작 이후 시간에 따라 점진 증가 (1.0 → 2.5 한계)
            const elapsed = (t - startedAt) / 1000; // sec
            const difficulty = Math.min(2.5, 1 + elapsed / 30); // 30초마다 +1씩, 최대 2.5배

            // spawn 주기 단축 (최대 3배 빨라짐)
            const baseInterval = 600; // ms
            const interval = Math.max(160, baseInterval / Math.min(3, difficulty + 0.5));
            if (t - lastSpawn > interval) { spawn(difficulty); lastSpawn = t; }

            // update player (touch control)
            if (touchState.current.left) player.vx = -4;
            else if (touchState.current.right) player.vx = 4;
            player.x += player.vx;
            if (player.x < 10) player.x = 10;
            if (player.x > W - 10) player.x = W - 10;

            // draw player
            ctx.fillStyle = '#22c55e';
            ctx.beginPath(); ctx.arc(player.x, player.y, player.r, 0, Math.PI * 2); ctx.fill();

            // update/draw obstacles
            ctx.fillStyle = '#ef4444';
            obstacles.forEach(o => { o.y += o.vy; });
            obstacles = obstacles.filter(o => o.y - o.r < H + 20);
            obstacles.forEach(o => { ctx.beginPath(); ctx.arc(o.x, o.y, o.r, 0, Math.PI * 2); ctx.fill(); });

            // collision
            for (const o of obstacles) {
                const dx = o.x - player.x, dy = o.y - player.y; const d2 = dx * dx + dy * dy;
                const rr = (o.r + player.r) * (o.r + player.r);
                if (d2 < rr) { alive = false; break; }
            }

            // score
            localScore += 1; if (localScore % 10 === 0) setScore(s => s + 1);

            if (!alive && !finalized) {
                finalized = true;
                setGameOver(true);
                // 약간의 지연 후 랭킹 저장 (UI 업데이트 보장)
                const finalScoreVal = Math.floor(localScore / 10);
                setTimeout(() => saveRanking(finalScoreVal), 0);
            }

            rafId = requestAnimationFrame(loop);
        };

        const keydown = (e) => {
            if (e.key === 'ArrowLeft') player.vx = -4;
            if (e.key === 'ArrowRight') player.vx = 4;
        };
        const keyup = (e) => {
            if (e.key === 'ArrowLeft' && player.vx < 0) player.vx = 0;
            if (e.key === 'ArrowRight' && player.vx > 0) player.vx = 0;
        };

        const resize = () => {
            ({ W, H } = getSize());
            canvas.width = W; canvas.height = H;
            player.y = H - 40;
        };

        window.addEventListener('keydown', keydown);
        window.addEventListener('keyup', keyup);
        window.addEventListener('resize', resize);
        setGameOver(false);
        setScore(0);
        setRunning(true);
        setIsSavingRanking(false);
        setSavedScore(null);
        rafId = requestAnimationFrame(loop);
        return () => { cancelAnimationFrame(rafId); window.removeEventListener('keydown', keydown); window.removeEventListener('keyup', keyup); window.removeEventListener('resize', resize); setRunning(false); };
    }, [isOpen, restartSeq, selectedGame, saveRanking]);


    if (!isOpen) return null;

    // 게임 선택 화면
    if (selectedGame === null) {
        return (
            <>
                <div className="fixed inset-0 z-[70] flex flex-col">
                    <div className="absolute inset-0 bg-black/60" onClick={onClose}></div>
                    <div className="relative flex-1 flex flex-col items-center justify-center p-4">
                        <div className="w-full max-w-[460px] bg-white rounded-2xl shadow-2xl p-4">
                            <div className="flex items-center justify-between mb-4">
                                <h3 className="text-xl font-bold text-gray-800">오락실</h3>
                                <button onClick={onClose} className="px-3 py-1 rounded-lg bg-gray-100 hover:bg-gray-200 transition-colors">
                                    닫기
                                </button>
                            </div>
                            <div className="space-y-3">
                                <button
                                    onClick={() => setSelectedGame('falling-dots')}
                                    className="w-full p-4 rounded-xl bg-gradient-to-r from-indigo-500 to-indigo-600 text-white hover:from-indigo-600 hover:to-indigo-700 transition-all shadow-lg hover:shadow-xl"
                                >
                                    <div className="text-left">
                                        <div className="font-bold text-lg mb-1">Falling Dots</div>
                                        <div className="text-sm opacity-90">떨어지는 점을 피하세요!</div>
                                    </div>
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
                <ShareBrowserModal
                    isOpen={showShareBrowserModal}
                    onClose={() => setShowShareBrowserModal(false)}
                />
            </>
        );
    }

    // Falling Dots 게임
    if (selectedGame === 'falling-dots') {
        return (
            <>
                <div className="fixed inset-0 z-[70] flex flex-col">
                    <div className="absolute inset-0 bg-black/60" onClick={onClose}></div>
                    <div className="relative flex-1 flex flex-col items-center justify-center p-4">
                        <div className="w-full max-w-[460px] bg-white rounded-2xl shadow-2xl p-3">
                            <div className="flex items-center justify-between mb-2">
                                <div className="flex items-center gap-2">
                                    <button
                                        onClick={() => setSelectedGame(null)}
                                        className="px-2 py-1 rounded bg-gray-100 hover:bg-gray-200 text-sm"
                                    >
                                        ← 게임 선택
                                    </button>
                                    <h3 className="font-bold text-gray-800">오락실 · Falling Dots</h3>
                                </div>
                                <button onClick={onClose} className="px-2 py-1 rounded bg-gray-100 hover:bg-gray-200">닫기</button>
                            </div>
                            <div className="mb-2 text-sm text-gray-600 flex items-center justify-between">
                                <span>터치 좌/우 버튼으로 이동! 점수: <span className="font-bold text-gray-900">{score}</span></span>
                                <button
                                    className="px-2 py-1 text-xs rounded bg-emerald-50 text-emerald-700 border border-emerald-200 hover:bg-emerald-100"
                                    onClick={() => { onClose?.(); setTimeout(() => { window.requestAnimationFrame(() => { /* reopen handled by parent if needed */ }); }, 50); }}
                                    title="종료"
                                >종료</button>
                            </div>
                            <div className="mb-2">
                                <button
                                    className="w-full py-2 text-sm rounded-lg bg-indigo-600 text-white hover:bg-indigo-700"
                                    onClick={() => { setRestartSeq(s => s + 1); setScore(0); }}
                                >재시작</button>
                            </div>
                            <canvas ref={canvasRef} className="w-full rounded border border-gray-200 block" style={{ maxHeight: '76vh' }} />
                            <div className="mt-3 grid grid-cols-2 gap-3 select-none pb-[env(safe-area-inset-bottom,0px)]">
                                <button
                                    className="py-3 rounded-lg bg-slate-800 text-white active:bg-slate-900"
                                    onTouchStart={() => (touchState.current.left = true)}
                                    onTouchEnd={() => (touchState.current.left = false)}
                                    onMouseDown={() => (touchState.current.left = true)}
                                    onMouseUp={() => (touchState.current.left = false)}
                                >왼쪽</button>
                                <button
                                    className="py-3 rounded-lg bg-slate-800 text-white active:bg-slate-900"
                                    onTouchStart={() => (touchState.current.right = true)}
                                    onTouchEnd={() => (touchState.current.right = false)}
                                    onMouseDown={() => (touchState.current.right = true)}
                                    onMouseUp={() => (touchState.current.right = false)}
                                >오른쪽</button>
                            </div>

                            {showResults && (() => {
                                const isInTop10 = rankings.some(r => r.name === playerName.trim() && r.score === finalScore);
                                return (
                                    <div className="absolute inset-0 bg-black/60 flex items-center justify-center p-4">
                                        <div className="bg-white rounded-2xl p-4 w-full max-w-[420px] shadow-xl">
                                            {isInTop10 && (
                                                <div className="mb-3 p-3 bg-gradient-to-r from-yellow-400 to-orange-400 rounded-lg text-center">
                                                    <div className="text-lg font-bold text-white">🎉 축하합니다! 🎉</div>
                                                    <div className="text-sm text-white mt-1">Top 10 랭킹에 진입하셨습니다!</div>
                                                </div>
                                            )}
                                            <div className="mb-3">
                                                <div className="text-sm font-semibold text-gray-700 mb-1">내 이름</div>
                                                <div className="flex gap-2">
                                                    <input
                                                        type="text"
                                                        value={playerName}
                                                        onChange={(e) => { setPlayerName(e.target.value); localStorage.setItem('arcadeName', e.target.value); }}
                                                        className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                                        placeholder="최대 12자"
                                                        maxLength={12}
                                                    />
                                                    <button
                                                        onClick={async () => {
                                                            // 이름 변경 후 랭킹 다시 저장 (중복 저장 방지 해제)
                                                            setSavedScore(null);
                                                            await saveRanking(finalScore);
                                                        }}
                                                        className="px-3 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 text-sm font-semibold whitespace-nowrap"
                                                    >
                                                        저장
                                                    </button>
                                                </div>
                                            </div>
                                            <h4 className="text-lg font-bold text-gray-800 mb-2">랭킹 Top 10</h4>
                                            <ol className="text-sm bg-gray-50 border border-gray-200 rounded-lg divide-y max-h-[50vh] overflow-auto">
                                                {rankings.map((r, idx) => {
                                                    const isMyRank = r.name === playerName.trim() && r.score === finalScore;
                                                    return (
                                                        <li 
                                                            key={r.ts + '' + idx} 
                                                            className={`px-3 py-2 flex justify-between ${isMyRank ? 'bg-yellow-50 font-semibold' : ''}`}
                                                        >
                                                            <span className={`${isMyRank ? 'text-indigo-700' : 'text-gray-700'}`}>
                                                                {idx + 1}. {r.name}
                                                            </span>
                                                            <span className={`font-semibold ${isMyRank ? 'text-indigo-900' : 'text-gray-900'}`}>
                                                                {r.score}
                                                            </span>
                                                        </li>
                                                    );
                                                })}
                                                {rankings.length === 0 && (
                                                    <li className="px-3 py-4 text-center text-gray-500">기록이 없습니다.</li>
                                                )}
                                            </ol>
                                            <div className="mt-3 space-y-2">
                                                <button
                                                    className="w-full py-2 rounded-lg bg-[#FEE500] text-black font-bold hover:bg-[#FDD835] transition-all duration-300 shadow-lg shadow-[#FEE500]/30 hover:shadow-[#FEE500]/50 flex items-center justify-center gap-2"
                                                    onClick={handleShareGameResult}
                                                >
                                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.367 2.684 3 3 0 00-5.367-2.684z" />
                                                    </svg>
                                                    {isInTop10 ? '🎉 랭킹 진입! 공유하기' : '카톡친구에게 공유하기'}
                                                </button>
                                                <div className="grid grid-cols-2 gap-2">
                                                    <button
                                                        className="py-2 rounded-lg bg-indigo-600 text-white hover:bg-indigo-700"
                                                        onClick={() => { setShowResults(false); setRestartSeq(s => s + 1); }}
                                                    >다시하기</button>
                                                    <button
                                                        className="py-2 rounded-lg bg-gray-100 text-gray-800 hover:bg-gray-200"
                                                        onClick={() => { setShowResults(false); onClose?.(); }}
                                                    >닫기</button>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })()}
                        </div>
                    </div>
                </div>
                <ShareBrowserModal
                    isOpen={showShareBrowserModal}
                    onClose={() => setShowShareBrowserModal(false)}
                />
            </>
        );
    }

    return null;
};

export default ArcadeModal;


